import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { sanitizeHtml } from '@/lib/sanitize-html';
import type {
  ContractTermsOverrideHandler,
  ProjectLayoutData,
  ProjectLayoutMode,
} from '../types';
import { capabilitiesForMode } from '../types';
import { DocumentLanguageDialog } from './DocumentLanguageDialog';
import { SectionHeading } from './SectionHeading';
import { Button } from '@/components/ui/button';

/**
 * Sprint 41 §14.S10 — Section IV "Contract Terms".
 *
 * Renders Handlebars-rendered SectionTemplate body (admin-managed
 * default) OR `Project.contractTermsOverride` per-project override
 * (§14.S10-Q2a + Q-S10-10).
 *
 * Founder mockup 2026-07-16 — collapsed-clauses idiom: the body is
 * split into clauses at its headings; each clause renders as a row
 * (all collapsed by default, "§ 4.n Title", tap to expand, plus an
 * Expand all / Collapse all control). Bodies without headings render
 * as a single expandable row. The clause split is display-only — the
 * stored body stays one HTML document.
 *
 * Visibility: per `capabilities.showContractTerms`. Per §14.S12-Q3
 * lock, this section is OMITTED in proposal mode. Estimate / review /
 * contract modes all render.
 *
 * Company default boilerplate is edited in the Open Document Language
 * dialog (stays on the estimate). Save writes Settings for future
 * contracts and applies the same language to this project. An existing
 * per-project override can still be reset to the company default.
 */
export interface SectionIV_ContractTermsProps {
  data: ProjectLayoutData;
  mode: ProjectLayoutMode;
  onContractTermsOverride?: ContractTermsOverrideHandler;
}

interface Clause {
  title: string;
  html: string;
}

/**
 * Split an HTML body into clauses at heading boundaries (h1–h6).
 * Content ahead of the first heading becomes a "General" clause.
 * Returns a single untitled clause when the body has no headings.
 */
export function splitHtmlIntoClauses(body: string, fallbackTitle: string): Clause[] {
  if (typeof DOMParser === 'undefined') {
    return [{ title: fallbackTitle, html: body }];
  }
  const doc = new DOMParser().parseFromString(body, 'text/html');
  const nodes = Array.from(doc.body.childNodes);
  const isHeading = (n: ChildNode): n is HTMLElement =>
    n.nodeType === 1 && /^H[1-6]$/.test((n as HTMLElement).tagName);

  if (!nodes.some(isHeading)) {
    return [{ title: fallbackTitle, html: body }];
  }

  const clauses: Clause[] = [];
  let current: { title: string; parts: string[] } | null = null;
  let leading: string[] = [];

  const flush = () => {
    if (current) {
      clauses.push({ title: current.title, html: current.parts.join('') });
      current = null;
    }
  };

  for (const node of nodes) {
    if (isHeading(node)) {
      flush();
      current = { title: node.textContent?.trim() || fallbackTitle, parts: [] };
    } else if (current) {
      current.parts.push(
        node.nodeType === 1
          ? (node as HTMLElement).outerHTML
          : (node.textContent ?? ''),
      );
    } else {
      const text =
        node.nodeType === 1
          ? (node as HTMLElement).outerHTML
          : (node.textContent ?? '');
      if (text.trim() !== '') leading.push(text);
    }
  }
  flush();

  if (leading.length > 0) {
    clauses.unshift({ title: 'General', html: leading.join('') });
    leading = [];
  }
  return clauses;
}

export function SectionIV_ContractTerms({
  data,
  mode,
  onContractTermsOverride,
}: SectionIV_ContractTermsProps) {
  const caps = capabilitiesForMode(mode);

  const sectionTemplates =
    (data.sectionTemplates as Array<{
      sectionCode: string;
      bodyHandlebars: string;
    }> | undefined) ?? [];
  const template = sectionTemplates.find(
    (t) => t.sectionCode === 'CONTRACT_TERMS',
  );
  const override = (data.project.contractTermsOverride as string | undefined) ?? null;
  const defaultBody = template?.bodyHandlebars ?? '';
  const body = override ?? defaultBody;

  const canResetOverride = caps.isEditable && onContractTermsOverride != null && override != null;
  const [busy, setBusy] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  const clauses = useMemo(
    () => (body ? splitHtmlIntoClauses(body, 'Terms & Conditions') : []),
    [body],
  );
  // Per-clause expansion — mockup: "all collapse by default".
  const [openClauses, setOpenClauses] = useState<ReadonlySet<number>>(new Set());
  const allOpen = clauses.length > 0 && openClauses.size === clauses.length;
  const toggleClause = (i: number) =>
    setOpenClauses((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const toggleAll = () =>
    setOpenClauses(allOpen ? new Set() : new Set(clauses.map((_, i) => i)));

  if (!caps.showContractTerms) return null;

  const reset = async () => {
    if (busy) return;
    if (!confirm('Reset Section IV to the admin-layer default? This clears the project override.')) {
      return;
    }
    setBusy(true);
    try {
      await onContractTermsOverride!(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="section-4"
      className="scroll-mt-24"
      data-mode={mode}
      data-section="IV"
      data-has-override={override != null ? 'true' : 'false'}
    >
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex items-baseline justify-between gap-3 px-6 pt-5 pb-1">
          <SectionHeading numeral="IV." title="Contract Terms" />
          {caps.isEditable && (
            <div className="flex items-baseline gap-3 text-sm">
              {canResetOverride && (
                <Button type="button" variant="ghost" size="sm"
                  onClick={reset}
                  disabled={busy}
                  className="h-auto px-1 py-0.5 text-sm font-semibold text-foreground-soft hover:text-destructive disabled:opacity-50">
                  Reset to default
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-1 py-0.5 text-sm font-semibold text-primary hover:text-primary-dim"
                onClick={() => setLanguageOpen(true)}
                data-testid="section-iv-open-document-language"
              >
                Open Document Language
              </Button>
            </div>
          )}
        </div>
        <p className="px-6 pb-4 font-public-sans text-xs text-muted-foreground">
          All clauses listed in order. Each is collapsed by default; tap a row to
          expand its full text. Open Document Language to edit the company
          default boilerplate without leaving this estimate.
        </p>

        <div className="px-6 pb-6">
          {clauses.length > 0 ? (
            <>
              <div className="divide-y divide-border rounded-md border border-border">
                {clauses.map((clause, i) => (
                  <ClauseRow
                    key={i}
                    ordinal={`§ 4.${i + 1}`}
                    title={clause.title}
                    html={clause.html}
                    open={openClauses.has(i)}
                    onToggle={() => toggleClause(i)}
                  />
                ))}
              </div>
              {/* Polish round (2026-07-23 #8) — at small widths the old
                  baseline-justified row interleaved the helper sentence
                  with the button label. Stack on mobile, row on sm+,
                  and drop the helper's duplicated button-name tail. */}
              <div className="mt-3 flex justify-end">
                <Button type="button" variant="ghost" size="sm"
                  onClick={toggleAll}
                  className="h-auto self-start px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim sm:self-auto"
                >
                  {allOpen ? 'Collapse all ▴' : 'Expand all ▾'}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              No contract terms configured.
            </p>
          )}
        </div>
      </div>
      {caps.isEditable && (
        <DocumentLanguageDialog
          open={languageOpen}
          onOpenChange={setLanguageOpen}
          projectOverride={override}
          onApplyToProject={onContractTermsOverride}
        />
      )}
    </section>
  );
}

/**
 * One clause row — shared by Sections IV and V. Collapsed: ordinal +
 * title + "expand" hint. Expanded: prose body.
 */
export function ClauseRow({
  ordinal,
  title,
  html,
  badge,
  open,
  onToggle,
}: {
  ordinal: string;
  title: string;
  html: string;
  badge?: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div data-clause-title={title}>
      <Button
        type="button"
        variant="ghost"
        onClick={onToggle}
        aria-expanded={open}
        className="flex h-auto w-full items-center justify-between gap-3 rounded-none px-4 py-3 text-left font-normal hover:bg-muted/30"
      >
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-public-sans text-xs font-bold text-primary">
            {ordinal}
          </span>
          <span className="truncate font-public-sans text-sm font-semibold text-foreground">
            {title}
          </span>
          {badge && (
            <span className="shrink-0 rounded-full bg-success/15 px-2 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-success">
              {badge}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1.5 font-public-sans text-[11px] text-muted-foreground">
          {open ? 'expanded' : 'expand'}
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </span>
      </Button>
      {open && (
        <div
          className="prose prose-slate max-w-none border-t border-border bg-muted/20 px-4 py-3 font-newsreader text-sm"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
        />
      )}
    </div>
  );
}
